import {z} from 'zod';
import {BUILDER_PROMPT_MAX_LENGTH} from '@packages/systems/ai/workflows/shared';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineToolRegistryManifest} from '../types/base-tool-types';

const toolName = 'edit-workflow' as const;

const argumentZod = z.object({
  workflowId: z
    .string()
    .describe('ID of the existing workflow to edit (from list-workflows).'),
  // Bounded to match the REST route: the tool wrappers call the handler
  // directly, so an unbounded string here would be a way around it.
  prompt: z
    .string()
    .trim()
    .min(1)
    .max(BUILDER_PROMPT_MAX_LENGTH)
    .describe(
      'Natural-language description of the change to apply, e.g. "point the HTTP request at https://example.org/hook" or "add a Slack message after the Set node".'
    ),
  // Optional, and mainly for programmatic MCP callers rather than the model: the
  // draft to edit plus the revision it read, so an edit lands in the draft the
  // caller is looking at and fails if that draft moved. Omitted -> the server
  // picks the workflow's open draft.
  draftId: z
    .string()
    .optional()
    .describe(
      "ID of the specific draft to edit. Omit to edit the workflow's current open draft."
    ),
  expectedRevision: z
    .number()
    .int()
    .positive()
    .optional()
    .describe(
      'Revision of draftId that the caller read. The edit is rejected if the draft has moved since. Requires draftId.'
    ),
  mutationId: z
    .string()
    .uuid()
    .optional()
    .describe(
      'Idempotency key. Reuse the same value when retrying a call whose response was lost, so the edit is applied once instead of creating a second draft.'
    ),
});

export const editWorkflowManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Edit AI workflow',
  family: 'workflows',
  llmTool: {
    name: toolName,
    description:
      "Edits an EXISTING AI workflow from a natural-language description of the change, preserving nodes the change does not affect. Use this instead of build-workflow whenever the user asks to change, fix, or add to a workflow that already exists. The change is written to the workflow's DRAFT and does NOT go live until the user saves it in the Designer, so tell the user their edit is ready to review and save. Returns the workflow ID plus the draft ID and revision the change landed in.",
    inputSchema: argumentZod,
  },
  type: 'server',
  featureGate: FEATURE_FLAGS.AIA_WORKFLOWS_MCP,
  confirmBeforeExecuting: false,
});

export type EditWorkflowArgumentType = z.infer<typeof argumentZod>;
